import classNames from "classnames";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { TestimonialCardType } from "./TestimonialCard";

export const CardStack = ({ items }: { items: TestimonialCardType[] }) => {
  const [cards, setCards] = useState<TestimonialCardType[]>(items);
  const isFirstRender = useRef(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      isFirstRender.current = false;
      setCards((prev) => {
        const newCards = [...prev];
        newCards.unshift(newCards.pop()!);
        return newCards;
      });
    }, 12000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return (
    <div className="t-relative t-h-[376px] t-w-72">
      {cards.map((card, index) => {
        const { name, image, title, quote, id } = card;
        const isFront = index === 0;
        const isSecond = index === 1;
        const isFourth = index === 3;

        const bgCardWidth = 1 + 0.06 * index;
        const bgCardHeight = 1 - 0.06 * index;

        const scaleX = isFront ? 1 : bgCardWidth;
        const scaleY = isFront ? 1 : bgCardHeight;
        const x = isFront && !isFirstRender.current ? [10, 0] : 0;

        const getStaticBackgroundColor = () => {
          if (index === 2) return "#4E3C89";
          return "#210B68";
        };

        return (
          <motion.div
            key={id}
            className="t-absolute t-h-[376px] t-w-72 t-rounded-3xl t-p-4 t-shadow-xl t-border t-border-neutral-200 dark:t-border-white/[0.1] t-shadow-black/[0.1] dark:t-shadow-white/[0.05] t-flex t-flex-col t-justify-between t-overflow-hidden"
            style={{
              transformOrigin: "center center",
              backgroundColor: isFront
                ? "#FFFFFF"
                : isSecond
                ? "#7A6DA7"
                : getStaticBackgroundColor(),
            }}
            initial={{
              x: 0,
              scaleX,
              scaleY,
              zIndex: cards.length - index,
              opacity: isFourth ? 0 : 1,
              backgroundColor: isFront
                ? "#FFFFFF"
                : isSecond
                ? "#7A6DA7"
                : getStaticBackgroundColor(),
            }}
            animate={{
              x,
              scaleX,
              scaleY,
              zIndex: cards.length - index,
              opacity: isFourth ? 0 : 1,
              backgroundColor:
                !isFirstRender.current && isSecond
                  ? ["#FFFFFF", "#7A6DA7"]
                  : !isFirstRender.current && isFront
                  ? ["#7A6DA7", "#FFFFFF"]
                  : isFront
                  ? "#FFFFFF"
                  : isSecond
                  ? "#7A6DA7"
                  : getStaticBackgroundColor(),
            }}
            transition={{
              type: "spring",
              stiffness: 120,
              damping: 60,
              scaleX: isFront ? { duration: 0 } : undefined,
              scaleY: isFront ? { duration: 0 } : undefined,
              backgroundColor: !isFirstRender.current
                ? { duration: 2, ease: "easeOut" }
                : { duration: 0 },
            }}
          >
            <motion.div
              className="t-px-8 t-py-10 t-h-[376px] t-flex t-flex-col t-items-center t-justify-between t-rounded-xl"
              initial={{
                opacity: isFront ? 1 : 0,
              }}
              animate={{
                opacity: isFront ? 1 : 0,
              }}
              transition={{
                duration: !isFirstRender.current ? 1 : 0,
                ease: "easeOut",
              }}
            >
              <div
                className={classNames("t-w-52 t-text-text-60 t-text-center", {
                  "t-text-text-lg": id === 1,
                  "t-text-subtext": id !== 1,
                })}
              >
                "{quote}"
              </div>
              <div className="t-flex t-items-center t-gap-[2px] t-flex-col">
                <div className="t-w-[52px] t-h-[52px] t-rounded-full t-aspect-square">
                  <img
                    src={image}
                    alt={name}
                    className="t-w-full t-h-full t-object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                      const parent = target.parentElement;
                      if (parent) {
                        parent.textContent = name
                          .split(" ")
                          .map((n) => n[0])
                          .join("");
                      }
                    }}
                  />
                </div>
                <div className="t-text-body-lg t-text-neutral-80">{name}</div>
                <div className="t-text-neutral-50 t-text-body-sm t-font-light">
                  {title}
                </div>
              </div>
            </motion.div>
          </motion.div>
        );
      })}
    </div>
  );
};
